import {
  Box,
  Button,
  HStack,
  Heading,
  Stack,
  VStack,
  chakra,
  useToast,
} from "@chakra-ui/react";
import {
  DeleteAction,
  FeatureAction,
  PerformerDraggable,
} from "./PerformerRow";
import { Reorder } from "framer-motion";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import actions from "../../../actions";
import {
  KYDPerformerDisplay,
  KYDPerformer,
  EventDetailLineupSection,
} from "@common/interfaces/KYDEvent";
import { useLoading } from "../../hooks/form";
import { EditModalType } from "../../../utils";
import { useDialog } from "../../../Common/Dialog";
import { SectionProps } from "../sections/sectionsComponents";
import GenresSection from "../Genres/GenresSection";
import { usePerformersSearch } from "../hooks/usePerformersSearch";
import SetTimeModal from "./SetTimeModal";
import dayjs from "dayjs";
import { FaPlus } from "react-icons/fa";
import DeleteScheduledUpdateDialog from "../ScheduledUpdates/DeleteScheduledUpdateDialog";
import useScheduledUpdates from "../ScheduledUpdates/hooks/useScheduledUpdates";
import { ScheduledUpdatesBadge } from "./ScheduledUpdatesBadge";
import {
  ScheduledUpdatesContext,
  withScheduledUpdates,
} from "../ScheduledUpdates/ScheduledUpdatesContext";
import UpcomingScheduledUpdateDialog, {
  UpcomingScheduledUpdateDialogContext,
  withUpcomingScheduledUpdateDialog,
} from "../ScheduledUpdates/UpcomingScheduledUpdateDialog";
import EventDetailTitle from "../../EventDetailTitle";

export type Performer = Partial<KYDPerformer>;

const List = chakra(Reorder.Group);

const LineupSection = ({
  data,
  setModalType,
  permissions,
  isLoading,
  refetchEvent,
}: SectionProps<EventDetailLineupSection["data"]>) => {
  const [newsOrder, setNewsOrder] = useState<string[]>([]);
  const newsOrderRef = useRef(null);
  const [lineup, setLineup] = useState<KYDPerformerDisplay[]>(
    data.performers || []
  );
  const dialog = useDialog();
  const toast = useToast();
  const [editPerformerModal, setEditPerformerModal] =
    useState<null | KYDPerformerDisplay>(null);
  const [setTimeValue, setSetTimeValue] = useState<string | null>(null);
  const { open: openScheduledUpdates, loading: scheduledUpdateLoading } =
    useContext(ScheduledUpdatesContext);
  const {
    state: { hasScheduledUpdatesForField, getScheduledUpdateForField },
  } = useScheduledUpdates();
  const {
    isOpen: isUpcomingScheduledUpdateDialogOpen,
    open: openUpcomingScheduledUpdateDialog,
    close: closeUpcomingScheduledUpdateDialog,
  } = useContext(UpcomingScheduledUpdateDialogContext);

  const {
    results: searchResults,
    search,
    loading: searchLoading,
    reset,
  } = usePerformersSearch(data.id);
  const [loading, updateLoading] = useLoading({
    performers: false,
    genres: false,
  });

  const onUpdatePerformers = useCallback(
    async (performers: KYDPerformerDisplay[]) => {
      try {
        await actions.ADMIN.updatePerformers(data.id, performers);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `Lineup updated`,
          description: `Lineup order has been updated`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        await refetchEvent({ silent: true });
        dialog({ text: err.message });
      }
    },
    [data, refetchEvent, setModalType]
  );

  const onDeletePerformer = useCallback(
    async (performer: KYDPerformerDisplay) => {
      try {
        await actions.ADMIN.deletePerformer(data.id, performer.id);
        await refetchEvent({ silent: true });
        setModalType(EditModalType.INACTIVE);

        toast({
          title: `${performer.name} removed`,
          description: `${performer.name} has been removed from the lineup`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
    },
    [data, setModalType, refetchEvent]
  );

  useEffect(() => {
    setNewsOrder(lineup.map((p) => p.id));
  }, [lineup]);

  useEffect(() => {
    if (data?.performers) {
      setLineup(data.performers);
    }
  }, [data]);

  useEffect(() => {
    //@ts-ignore
    newsOrderRef.current = newsOrder;
  }, [newsOrder]);

  const onUpdateOrder = async () => {
    const newOrder = newsOrderRef.current;
    const performersInNewOrder = newOrder.map((no) =>
      lineup.find((p) => p.id === no)
    );

    setLineup(performersInNewOrder);
    updateLoading("performers", true);

    await onUpdatePerformers(performersInNewOrder);

    updateLoading("performers", false);
  };

  const onSearch = async (query: string) => {
    search(query);
  };

  const onUpdateGenres = useCallback(
    async (genres: { name: string; id?: string }[]) => {
      try {
        updateLoading("genres", true);
        await actions.ADMIN.updateGenres(data.id, genres);
        await refetchEvent({ silent: true });

        toast({
          title: `Genres updated!`,
          description: `The genres of your event has been updated.`,
          status: "success",
          duration: 5000,
          isClosable: true,
        });
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("genres", false);
    },
    [data.id, updateLoading]
  );

  const onClickFeature = async (
    performer: KYDPerformerDisplay,
    isHeadliner: boolean
  ) => {
    const newPerformers = lineup.map((oldP) => {
      if (oldP.id === performer.id) {
        return {
          ...performer,
          headliner: isHeadliner,
        };
      }

      return oldP;
    });

    setLineup(newPerformers);

    updateLoading("performers", true);
    await onUpdatePerformers(newPerformers);
    updateLoading("performers", false);
  };

  const handleOpenSetTime = (performer: KYDPerformerDisplay) => {
    setEditPerformerModal(performer);
    setSetTimeValue(performer.set_time || null);
  };

  const handleCloseSetTime = () => {
    setEditPerformerModal(null);
    setSetTimeValue(null);
  };

  const handleSaveSetTime = async (data: {
    time: dayjs.Dayjs | null;
    id: string;
  }) => {
    const updatedLineup = lineup.map((p) =>
      p.id === data.id
        ? { ...p, set_time: data.time?.toISOString() || undefined }
        : p
    );
    console.log({ updatedLineup, lineup, data });
    setLineup(updatedLineup);
    handleCloseSetTime();
    updateLoading("performers", true);
    await onUpdatePerformers(updatedLineup);
    updateLoading("performers", false);
  };

  const hasPermission = (permissionToCheck) =>
    permissions.includes(permissionToCheck);

  const onClickDelete = async (performer: KYDPerformerDisplay) => {
    const newPerformers = lineup.filter((oldP) => oldP.id !== performer.id);
    setLineup(newPerformers);
    updateLoading("performers", true);

    await onDeletePerformer(performer);

    updateLoading("performers", false);
  };

  return (
    <VStack w="full">
      <DeleteScheduledUpdateDialog
        isLoading={scheduledUpdateLoading.scheduledUpdate}
      />
      <UpcomingScheduledUpdateDialog
        isLoading={isLoading}
        onEditScheduledUpdate={() => {
          const scheduledUpdate = getScheduledUpdateForField(
            data.scheduled_updates,
            "performers"
          );

          closeUpcomingScheduledUpdateDialog();
          if (scheduledUpdate) {
            openScheduledUpdates({ scheduledUpdate, field: "performers" });
          }
        }}
      />
      <Stack
        direction={["column", "row"]}
        justify={"space-between"}
        alignItems={["flex-start", "center"]}
        gap={[3, 0]}
        w="full"
      >
        <HStack justifyContent={"center"} gap={2}>
          <EventDetailTitle>Lineup</EventDetailTitle>
          <ScheduledUpdatesBadge
            scheduledUpdates={data.scheduled_updates}
            field="performers"
            hasScheduledUpdatesForField={hasScheduledUpdatesForField}
          />
        </HStack>

        <Button
          aria-label="Add performer"
          backgroundColor={"black"}
          color={"white"}
          size="sm"
          rightIcon={<FaPlus />}
          px={8}
          onClick={() => {
            openScheduledUpdates({
              field: "performers",
              afterSave: async () => {
                console.log("afterSave called", data.scheduled_updates);
                // const scheduledUpdate = getScheduledUpdateForField(
                //   data.scheduled_updates,
                //   "performers"
                // );

                // if (scheduledUpdate) {
                //   console.log("opening upcoming scheduled update dialog");
                //   openUpcomingScheduledUpdateDialog();
                // }
              },
            });
          }}
        >
          Add perfomer
        </Button>
      </Stack>
      {newsOrder.length ? (
        <List
          draggable={false}
          axis="y"
          onReorder={setNewsOrder}
          values={newsOrder}
          listStyleType={"none"}
          w="full"
        >
          <VStack
            my={4}
            sx={{
              opacity: loading.performers ? 0.5 : 1,
              pointerEvents: loading.performers ? "none" : "auto",
            }}
          >
            {newsOrder
              .map((no) => lineup.find((p) => p.id === no))
              .map((p) =>
                p ? (
                  <PerformerDraggable
                    readOnly={!hasPermission("write")}
                    onUpdate={hasPermission("write") ? onUpdateOrder : () => {}}
                    key={`${p.id}-${p.name}`}
                    name={p.name}
                    platforms={[p.instagram, p.spotify, p.tiktok]}
                    image={p.image_url}
                    id={p.id}
                    timezone={data.timezone}
                    actions={
                      hasPermission("write") ? (
                        <HStack ml={["auto", 0]} gap={2}>
                          <FeatureAction
                            key={`feature-${p.id}`}
                            isActive={p.headliner}
                            isDisabled={isLoading || loading.performers}
                            onClick={() => onClickFeature(p, !p.headliner)}
                            name={p.name}
                          />
                          ,
                          <Box backgroundColor="gray.200" w={"1px"} h={6} />,
                          <DeleteAction
                            key={`delete-${p.id}`}
                            isDisabled={isLoading || loading.performers}
                            onClick={() => onClickDelete(p)}
                            name={p.name}
                          />
                          ,
                        </HStack>
                      ) : (
                        []
                      )
                    }
                    // Pass set time props
                    setTime={p.set_time}
                    onSetTime={
                      hasPermission("write")
                        ? () => handleOpenSetTime(p)
                        : undefined
                    }
                    canSetTime={hasPermission("write")}
                  />
                ) : null
              )}
          </VStack>
        </List>
      ) : null}
      {/* Set Time Modal */}
      <SetTimeModal
        isOpen={!!editPerformerModal}
        onClose={handleCloseSetTime}
        onSave={handleSaveSetTime}
        performer={editPerformerModal}
        value={setTimeValue}
        onChange={setSetTimeValue}
        loading={loading.performers}
        timezone={data.timezone}
      />
      <GenresSection
        readOnly={!hasPermission("write")}
        data={{
          genres: data.genres,
        }}
        isLoading={isLoading || loading.genres}
        onSave={onUpdateGenres}
      />
    </VStack>
  );
};

export default withUpcomingScheduledUpdateDialog(
  withScheduledUpdates(LineupSection)
);
